import { ReactNode, useEffect, useState, useRef } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '@/services/supabase'
import { useAuth } from '@/context/auth-context'
import { isSessionVerified, markSessionVerified, clearSessionVerified } from '@/utils/session-utils'

interface ProtectedRouteProps {
  children: ReactNode
  redirectTo?: string
}

/**
 * A component that protects routes requiring authentication
 * Redirects to login page if user is not authenticated
 */
function ProtectedRoute({ children, redirectTo = '/login' }: ProtectedRouteProps) {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null)
  // Add a ref to track if we've completed verification
  const verificationCompleteRef = useRef(false)

  const {
    isAuthenticated,
    isLoading,
    error: authContextError,
    success: authContextSuccess,
  } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // Check for previously verified session on component mount
  useEffect(() => {
    if (isSessionVerified()) {
      verificationCompleteRef.current = true
      setIsCheckingAuth(false)
    }
  }, [])

  useEffect(() => {
    let mounted = true

    // Function to create a safety timeout
    const createSafetyTimeout = () => {
      const id = setTimeout(() => {
        if (mounted && isCheckingAuth) {
          setIsCheckingAuth(false)
        }
      }, 3000) // 3 second timeout
      setTimeoutId(id)
      return id
    }

    const verifySession = async () => {
      // Skip verification if we've already completed it successfully
      if (verificationCompleteRef.current) {
        setIsCheckingAuth(false)
        return
      }

      // Set a safety timeout to prevent infinite loading
      const timeoutId = createSafetyTimeout()

      try {
        const { data, error } = await supabase.auth.getSession()

        if (error) {
          if (mounted) {
            setAuthError(error.message)
            setIsCheckingAuth(false)
          }
          return
        }

        if (data.session) {
          // Session exists, mark verification as complete
          verificationCompleteRef.current = true
          markSessionVerified()

          if (mounted) {
            setIsCheckingAuth(false)
          }
        } else {
          // No session, clear verification flag and redirect to login
          clearSessionVerified()

          if (mounted) {
            setIsCheckingAuth(false)
            navigate(redirectTo, { state: { from: location }, replace: true })
          }
        }
      } catch (error) {
        if (mounted) {
          setAuthError(
            error instanceof Error ? error.message : 'Authentication verification failed'
          )
          setIsCheckingAuth(false)
        }
      } finally {
        // Clear timeout if verification completes
        if (timeoutId) clearTimeout(timeoutId)
      }
    }

    // Only run verification if auth context is still loading and we haven't already verified
    if (isLoading && !verificationCompleteRef.current) {
      verifySession()
    } else if (!isLoading) {
      setIsCheckingAuth(false)

      // If user is authenticated, mark verification as complete
      if (isAuthenticated) {
        verificationCompleteRef.current = true
        markSessionVerified()
      } else {
        // Clear verification flag if not authenticated
        clearSessionVerified()
      }
    }

    // Handle tab visibility changes
    const handleVisibilityChange = () => {
      // Only re-check when tab becomes visible and we have already verified once
      if (document.visibilityState === 'visible' && verificationCompleteRef.current) {
        // Don't set loading state again to avoid flickering UI
        supabase.auth.getSession().then(({ data, error }) => {
          if (error) {
            // Handle error silently
          } else if (!data.session && mounted) {
            // Clear verification flag
            clearSessionVerified()
            navigate(redirectTo, { state: { from: location }, replace: true })
          } else if (data.session) {
            // Ensure the verification flag is set
            markSessionVerified()
          }
        })
      }
    }

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      mounted = false
      if (timeoutId) clearTimeout(timeoutId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [
    location.pathname,
    isLoading,
    redirectTo,
    navigate,
    location,
    isAuthenticated,
    isCheckingAuth,
  ])

  // If still checking authentication, show loading spinner
  if (isCheckingAuth || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="mt-4 text-text">Loading user data...</p>
        <p className="text-sm text-text-dark mt-2">Please wait while we verify your session</p>
      </div>
    )
  }

  // If there was an error during authentication check
  if (authError || authContextError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <div className="bg-red-900/20 border border-red-800 text-red-100 px-6 py-4 rounded-lg max-w-md">
          <h2 className="text-xl font-bold mb-2">Authentication Error</h2>
          <p>{authError || authContextError}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary/80"
          >
            Refresh Page
          </button>
        </div>
      </div>
    )
  }

  // If there's a success message, display it
  if (authContextSuccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <div className="bg-green-900/20 border border-green-800 text-green-100 px-6 py-4 rounded-lg max-w-md">
          <h2 className="text-xl font-bold mb-2">Success</h2>
          <p>{authContextSuccess}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary/80"
          >
            Refresh Page
          </button>
        </div>
      </div>
    )
  }

  // If authenticated redirect to requested page
  if (isAuthenticated) {
    return <>{children}</>
  }

  // If not authenticated, redirect to login page
  return <Navigate to={redirectTo} state={{ from: location }} replace />
}

export { ProtectedRoute }
